import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Check } from "lucide-react";
import { fadeUp, stagger } from "../lib/motion";
import { LogoMark } from "./Logo";

const POINTS = [
  "No jargon, no fluff — just systems that move revenue.",
  "Founder-led strategy. No agency runaround.",
  "Transparent reporting tied to booked calls and closed deals.",
  "Built to scale with you, from first launch to market leader.",
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-60, 60]);
  const [photoOk, setPhotoOk] = useState(true);

  return (
    <section id="about" className="relative py-28 lg:py-36 panel-grid" ref={ref} data-testid="about-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 relative">
          <div
            className="relative h-[440px] lg:h-[560px] overflow-hidden border border-[#2A2D34] bg-[#0A0C0F]"
            data-testid="founder-photo-frame"
          >
            {photoOk ? (
              <img
                src="/founder.jpg"
                alt="Camy Anthony — Founder of HughLaunch"
                loading="lazy"
                decoding="async"
                onError={() => setPhotoOk(false)}
                className="absolute inset-0 h-full w-full object-contain sm:object-cover"
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-6">
                <LogoMark className="h-24 w-24 opacity-90" />
                <p className="text-2xl font-black uppercase tracking-tight text-white">Camy Anthony</p>
                <p className="-mt-4 text-xs font-bold uppercase tracking-[0.25em] text-[#8A939B]">Founder · Brand Strategist · Web Designer</p>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#111317] via-transparent to-transparent pointer-events-none" />
            {photoOk && (
              <div className="absolute bottom-4 right-4 max-w-[150px] sm:bottom-5 sm:right-5 sm:max-w-[230px] text-right">
                <p className="text-base sm:text-lg font-black uppercase tracking-tight text-white leading-none">Camy Anthony</p>
                <p className="mt-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] text-[#C6FF00] leading-relaxed">Founder · Brand Strategist · Web Designer</p>
              </div>
            )}
          </div>
          <div className="absolute -bottom-6 -left-3 sm:-left-10 border border-[#C6FF00] bg-[#0A0C0F] px-4 py-3 sm:px-6 sm:py-5 lime-glow">
            <p className="text-xl sm:text-3xl font-black tracking-tight leading-none text-[#C6FF00]">Founder-Led</p>
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider mt-1 text-white">No agency runaround</p>
          </div>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          variants={stagger}
          className="lg:col-span-7 lg:pl-8"
        >
          <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.3em] text-[#C6FF00] mb-5">
            / The Founder
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="text-4xl lg:text-6xl font-black uppercase tracking-tighter leading-[0.95] mb-8"
            data-testid="founder-heading"
          >
            Meet the strategist<br />behind HughLaunch.
          </motion.h2>
          <motion.blockquote
            variants={fadeUp}
            className="mb-8 border-l-2 border-[#C6FF00] pl-6 text-xl lg:text-2xl font-bold tracking-tight leading-snug text-white max-w-2xl"
            data-testid="founder-belief"
          >
            &ldquo;I built HughLaunch around one belief: a great business shouldn&rsquo;t
            lose customers because its digital presence makes it look smaller than
            it really is.&rdquo;
          </motion.blockquote>
          <motion.p variants={fadeUp} className="text-lg text-[#8A939B] font-medium leading-relaxed max-w-2xl mb-10">
            I became obsessed with a question too big to answer in a sentence:
            why do people trust one business and scroll past another? I spent
            two years educating myself, testing systems through trial and
            error, and mastering the craft behind branding, websites and
            customer acquisition. Every HughLaunch build runs through the same
            lens: make your work look as good as it actually is, then wire it to
            a system that turns that attention into booked clients. You work
            directly with me, from first strategy call to launch.
          </motion.p>
          <div className="grid sm:grid-cols-2 gap-5">
            {POINTS.map((p) => (
              <motion.div key={p} variants={fadeUp} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#C6FF00]">
                  <Check className="h-4 w-4 text-[#0A0C0F]" strokeWidth={3} />
                </span>
                <span className="text-white/90 font-medium">{p}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
